/**
 * @fileoverview Syndicator Showdown — Admin Ticker Control Panel
 * @module components/admin/TickerPanel
 *
 * Sits at the top of AdminSlatesPage (above the import panel).
 * Writes to Firestore game_config/ticker — MarqueeTicker reads it live.
 *
 * Fields:
 *   enabled  — toggle ticker on/off
 *   message  — the scrolling text
 *   color    — text colour (LED colours presets + custom picker)
 *   speed    — 0.1–3.0 multiplier (1.0 = default)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import './TickerPanel.css';

const DEFAULT_CONFIG = {
  enabled: true,
  message: '',
  color:   '#FF3333',
  speed:   1.0,
};

// Preset LED colours — racecourse/TAB palette
const COLOR_PRESETS = [
  { label: 'Red',    value: '#FF3333' },
  { label: 'Amber',  value: '#FFB800' },
  { label: 'Green',  value: '#00FF88' },
  { label: 'Cyan',   value: '#00DFFF' },
  { label: 'White',  value: '#FFFFFF' },
  { label: 'Gold',   value: '#E6B800' },
  { label: 'Pink',   value: '#FF44AA' },
];

export const TickerPanel = () => {
  const [config,  setConfig]  = useState(DEFAULT_CONFIG);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [localMsg, setLocalMsg] = useState('');

  // Load existing config from Firestore
  useEffect(() => {
    const ref = doc(db, 'game_config', 'ticker');
    return onSnapshot(ref, snap => {
      if (snap.exists()) {
        const data = { ...DEFAULT_CONFIG, ...snap.data() };
        setConfig(data);
        setLocalMsg(data.message || '');
      } else {
        setLocalMsg('');
      }
    });
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'game_config', 'ticker'), {
        ...config,
        message: localMsg.trim(),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('[TickerPanel] Save failed:', err);
    } finally {
      setSaving(false);
    }
  }, [config, localMsg]);

  const update = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  };

  const speedLabel = (s) => {
    if (s <= 0.4)  return 'Very slow';
    if (s <= 0.7)  return 'Slow';
    if (s <= 1.3)  return 'Normal';
    if (s <= 2.0)  return 'Fast';
    return 'Very fast';
  };

  return (
    <div className="ss-ticker-panel">

      {/* Header */}
      <div className="ss-ticker-panel-header">
        <div className="ss-ticker-panel-title-row">
          <h2 className="ss-ticker-panel-title">📺 Homepage Ticker</h2>
          {/* Enable toggle */}
          <label className="ss-ticker-toggle">
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={e => update('enabled', e.target.checked)}
            />
            <span className="ss-ticker-toggle-track" />
            <span className="ss-ticker-toggle-label">
              {config.enabled ? 'Visible' : 'Hidden'}
            </span>
          </label>
        </div>
        <p className="ss-ticker-panel-desc">
          Scrolls across the landing page below the header. Updates live — no deploy needed.
        </p>
      </div>

      {/* Live preview */}
      <div className="ss-ticker-preview-wrap">
        <div className="ss-ticker-preview">
          <span className="ss-ticker-preview-label">LIVE</span>
          <div className="ss-ticker-preview-track">
            {localMsg.trim() ? (
              <span
                className="ss-ticker-preview-text"
                style={{
                  color: config.color,
                  filter: `drop-shadow(0 0 5px ${config.color})`,
                  animationDuration: `${Math.round(40 / config.speed)}s`,
                }}
              >
                {localMsg.trim()}     ✦     {localMsg.trim()}     ✦     {localMsg.trim()}
              </span>
            ) : (
              <span className="ss-ticker-preview-empty">
                Enter a message below to preview…
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Message input */}
      <div className="ss-ticker-field">
        <label className="ss-ticker-field-label">Message</label>
        <textarea
          className="ss-ticker-textarea"
          value={localMsg}
          onChange={e => { setLocalMsg(e.target.value); setSaved(false); }}
          placeholder="GAME COMING SOON. Currently in development. Advertisers and sponsors welcome."
          rows={2}
          maxLength={300}
        />
        <div className="ss-ticker-char-count">{localMsg.length}/300</div>
      </div>

      {/* Colour */}
      <div className="ss-ticker-field">
        <label className="ss-ticker-field-label">Text Colour</label>
        <div className="ss-ticker-color-row">
          {COLOR_PRESETS.map(preset => (
            <button
              key={preset.value}
              type="button"
              className={`ss-ticker-color-swatch ${config.color === preset.value ? 'active' : ''}`}
              style={{ background: preset.value }}
              title={preset.label}
              onClick={() => update('color', preset.value)}
            />
          ))}
          {/* Custom colour picker */}
          <div className="ss-ticker-color-custom">
            <input
              type="color"
              value={config.color}
              onChange={e => update('color', e.target.value)}
              title="Custom colour"
              className="ss-ticker-color-input"
            />
            <span className="ss-ticker-color-hex">{config.color}</span>
          </div>
        </div>
      </div>

      {/* Speed */}
      <div className="ss-ticker-field">
        <label className="ss-ticker-field-label">
          Scroll Speed
          <span className="ss-ticker-speed-val">
            {config.speed.toFixed(1)}× — {speedLabel(config.speed)}
          </span>
        </label>
        <div className="ss-ticker-speed-row">
          <span className="ss-ticker-speed-hint">Slow</span>
          <input
            type="range"
            min="0.1"
            max="3.0"
            step="0.1"
            value={config.speed}
            onChange={e => update('speed', parseFloat(e.target.value))}
            className="ss-ticker-slider"
          />
          <span className="ss-ticker-speed-hint">Fast</span>
        </div>
      </div>

      {/* Save button */}
      <div className="ss-ticker-save-row">
        {saved && <span className="ss-ticker-saved">✓ Saved — ticker updated live</span>}
        <button
          type="button"
          className="ss-ticker-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? '⏳ Saving…' : '💾 Save & Publish'}
        </button>
      </div>

    </div>
  );
};

export default TickerPanel;
